/**
 * @voltxampere/ui — Clipboard helpers (Sprint 13)
 *
 * Copy / paste of a circuit fragment via the system clipboard.
 * Per ADR-0019, the canonical MIME type is
 * `application/x-vxa-circuit` carrying a JSON-serialised
 * `ClipboardPayload`. A `text/plain` fallback ships the same
 * JSON so a paste into a non-VXA target still surfaces something
 * intelligible (and so non-`ClipboardItem` browsers degrade
 * gracefully).
 *
 * `regenerateIds` rewrites every component / wire / net id so a
 * paste into the same circuit cannot collide with the original.
 *
 * ⚠️ TODO(sprint-19): the schema-version literal here pins the
 * fragment to schema 0.4.0 manually. Sprint 19 persistence will
 * publish a `@voltxampere/schema/version` sub-export the canvas
 * can import without dragging the entire Zod barrel into the
 * apps/web bundle (see Sprint 12 D2).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
export const VXA_CLIPBOARD_MIME = "application/x-vxa-circuit";
export const VXA_CLIPBOARD_KIND = "vxa-circuit-fragment";
/**
 * ⚠️ TODO(sprint-19): replace with
 *   `import { SCHEMA_VERSION } from "@voltxampere/schema/version"`.
 * The hardcoded literal sidesteps Sprint 12 D2 — pulling the
 * full schema barrel for a single string would re-bundle Zod.
 */
const SCHEMA_VERSION_HARDCODE = "0.4.0";
function getClipboard() {
    if (typeof navigator === "undefined")
        return null;
    const c = navigator.clipboard;
    return c ?? null;
}
/**
 * Wrap the fragment in a payload and write it to the system
 * clipboard. Throws on a clean failure path (no clipboard access
 * granted, both modern and text APIs unavailable). Tests stub
 * `navigator.clipboard` to bypass user-gesture requirements.
 */
export async function copyToClipboard(fragment) {
    const payload = {
        schemaVersion: SCHEMA_VERSION_HARDCODE,
        type: VXA_CLIPBOARD_KIND,
        components: fragment.components,
        wires: fragment.wires,
        nets: fragment.nets,
        bbox: fragment.bbox,
        copiedAt: Date.now(),
    };
    const json = JSON.stringify(payload);
    const clipboard = getClipboard();
    if (!clipboard) {
        throw new Error("[VXA clipboard] navigator.clipboard unavailable");
    }
    // Modern path — ClipboardItem with custom MIME.
    if (typeof ClipboardItem !== "undefined" && clipboard.write) {
        try {
            const item = new ClipboardItem({
                [VXA_CLIPBOARD_MIME]: new Blob([json], { type: VXA_CLIPBOARD_MIME }),
                "text/plain": new Blob([json], { type: "text/plain" }),
            });
            await clipboard.write([item]);
            return;
        }
        catch {
            // Fall through to writeText.
        }
    }
    if (clipboard.writeText) {
        await clipboard.writeText(json);
        return;
    }
    throw new Error("[VXA clipboard] neither write nor writeText available");
}
/**
 * Read a clipboard fragment if one is present. Returns `null`
 * when the clipboard does not carry a VXA payload (or when the
 * browser denies access). Never throws on the clean miss path.
 */
export async function readFromClipboard() {
    const clipboard = getClipboard();
    if (!clipboard)
        return null;
    // Modern path — read with MIME negotiation.
    if (clipboard.read) {
        try {
            const items = await clipboard.read();
            for (const item of items) {
                if (item.types.includes(VXA_CLIPBOARD_MIME)) {
                    const blob = await item.getType(VXA_CLIPBOARD_MIME);
                    const text = await blob.text();
                    const parsed = parsePayload(text);
                    if (parsed)
                        return parsed;
                }
            }
        }
        catch {
            // fall through to text fallback
        }
    }
    if (clipboard.readText) {
        try {
            const text = await clipboard.readText();
            return parsePayload(text);
        }
        catch {
            return null;
        }
    }
    return null;
}
function parsePayload(text) {
    try {
        const raw = JSON.parse(text);
        if (typeof raw === "object" &&
            raw !== null &&
            "type" in raw &&
            raw.type === VXA_CLIPBOARD_KIND) {
            return raw;
        }
        return null;
    }
    catch {
        return null;
    }
}
const PASTE_NET_SUFFIX_RE = /_paste[a-z0-9]+$/u;
/**
 * Rewrite every id in the payload to a unique value. Components
 * and wires get fresh `crypto.randomUUID`s; nets keep their
 * original prefix and get a `_pasteXXXX` suffix so a paste into
 * the same circuit can still tell which net came from where.
 *
 * Wire `netId` and any nested net references are remapped through
 * the same id table so connectivity survives the regen.
 *
 * Idempotent on the input — the payload object is not mutated.
 */
export function regenerateIds(payload) {
    const idMap = new Map();
    const components = payload.components.map((c) => {
        const fresh = crypto.randomUUID();
        idMap.set(c.id, fresh);
        return { ...c, id: fresh };
    });
    // Net suffix — deterministic per-call but unique between calls,
    // and idempotent across repeated pastes (each paste gets its
    // own random tail).
    const tail = crypto.randomUUID().slice(0, 4);
    const nets = payload.nets.map((n) => {
        const baseId = n.id.replace(PASTE_NET_SUFFIX_RE, "");
        const fresh = `${baseId}_paste${tail}`;
        idMap.set(n.id, fresh);
        return { ...n, id: fresh };
    });
    const wires = payload.wires.map((w) => {
        const fresh = crypto.randomUUID();
        return {
            ...w,
            id: fresh,
            netId: idMap.get(w.netId) ?? w.netId,
        };
    });
    return {
        ...payload,
        components,
        nets,
        wires,
    };
}
//# sourceMappingURL=clipboard.js.map